import type { LiveReviewInteractions } from "@common/components/chat/liveReviewInteractions";
import type { NormalizedComment, NormalizedIssue } from "@common/types/issue";
import { buildActionId, buildConcedeId, liveReviewIssueKey } from "@common/utils/liveReviewOoxml";

export interface SupersedingIssueView {
  label: string;
  onShow: (() => void) | null;
}

export interface IssueCommentWithoutReason {
  kind: "dismissed" | "applied" | "pending";
}

export interface IssueCommentHeldBack {
  kind: "heldBack";
  heldBackReason: string;
}

export interface IssueCommentFailed {
  kind: "failed";
  failureReason: string;
}

export type IssueCommentState = IssueCommentWithoutReason | IssueCommentHeldBack | IssueCommentFailed;

export type IssueCommentStateReads = Pick<LiveReviewInteractions, "isActionApplied" | "isActionDismissed" | "commentHeldBackReason" | "commentFailureReasonOf" | "partlyAppliedReasonOf">;

export const COMMENT_WAITS_FOR_ITS_NEW_PARAGRAPH_MESSAGE = "Apply the redline first: this comment goes on the paragraph it adds.";

export function commentSitsOnTheParagraphItsRemedyAdds(issueComment: NormalizedComment, issue: NormalizedIssue): boolean {
  if (issueComment.placement === "after_paragraph") return true;
  return (issue.remedies ?? []).some((remedy) => (remedy.textRedline?.insertions ?? []).some((insertion) => insertion.bookmark === issueComment.bookmark));
}

export function remedyLandedItsParagraphs(remedyActionId: string, conversationId: string, interactions: Pick<IssueCommentStateReads, "isActionApplied" | "partlyAppliedReasonOf">): boolean {
  return interactions.isActionApplied(conversationId, remedyActionId) || (interactions.partlyAppliedReasonOf?.(conversationId, remedyActionId) ?? null) !== null;
}

export function issueCommentStateOf(
  issue: NormalizedIssue,
  issueComment: NormalizedComment,
  commentActionId: string | null,
  remedyActionId: string | null,
  conversationId: string,
  interactions: IssueCommentStateReads,
): IssueCommentState {
  if (commentActionId !== null && interactions.isActionDismissed(conversationId, commentActionId)) return { kind: "dismissed" };
  if (commentActionId !== null && interactions.isActionApplied(conversationId, commentActionId)) return { kind: "applied" };
  if (commentSitsOnTheParagraphItsRemedyAdds(issueComment, issue) && remedyActionId !== null && !remedyLandedItsParagraphs(remedyActionId, conversationId, interactions)) {
    return { kind: "heldBack", heldBackReason: COMMENT_WAITS_FOR_ITS_NEW_PARAGRAPH_MESSAGE };
  }
  const heldBackReason = interactions.commentHeldBackReason?.(issue, issueComment) ?? null;
  if (heldBackReason !== null) return { kind: "heldBack", heldBackReason };
  const failureReason = commentActionId === null ? null : interactions.commentFailureReasonOf?.(conversationId, commentActionId) ?? null;
  if (failureReason !== null) return { kind: "failed", failureReason };
  return { kind: "pending" };
}

export function supersedingIssueViewOf(
  supersedingIssueKey: string,
  reviewIssues: NormalizedIssue[],
  listedIssues: NormalizedIssue[],
  showIssueAt: (listedIndex: number) => void,
): SupersedingIssueView {
  const supersedingIssue = reviewIssues.find((issue) => liveReviewIssueKey(issue) === supersedingIssueKey);
  const listedIndex = listedIssues.findIndex((issue) => liveReviewIssueKey(issue) === supersedingIssueKey);
  return {
    label: supersedingIssue?.name ?? supersedingIssueKey,
    onShow: listedIndex === -1 ? null : () => showIssueAt(listedIndex),
  };
}

export function withLocalActionState(
  issue: NormalizedIssue,
  conversationId: string,
  messageId: string | undefined,
  interactions: Pick<LiveReviewInteractions, "isActionApplied" | "isActionDismissed" | "getActionSupersession"> | undefined,
): NormalizedIssue {
  if (!interactions) return issue;
  const issueKey = liveReviewIssueKey(issue);
  const primaryRemedy = issue.remedies?.[0];
  const primaryComment = issue.comments?.[0];
  const remedyActionId = primaryRemedy ? buildActionId(messageId, issueKey, "remedy", primaryRemedy.id) : null;
  const remedyApplied = !!remedyActionId && interactions.isActionApplied(conversationId, remedyActionId);
  const remedySuperseded = !!remedyActionId && !!interactions.getActionSupersession(conversationId, remedyActionId);
  const commentApplied =
    !!primaryComment &&
    interactions.isActionApplied(conversationId, buildActionId(messageId, issueKey, "comment", primaryComment.id));
  return {
    ...issue,
    responded: issue.responded || remedyApplied || (commentApplied && !remedySuperseded),
    conceded: issue.conceded || interactions.isActionDismissed(conversationId, buildConcedeId(messageId, issueKey)),
  };
}
